import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react'
import { FaCheck, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaInfoCircle, FaTimesCircle } from 'react-icons/fa'
import { Button } from '../Button'
import { cx } from '../cx'
import s from './styles.module.scss'

// ---------------------------------------------------------------- InfoTip
export interface InfoTipProps {
    /** The explanation shown on hover / focus and read by screen readers. */
    text: string
    className?: string
}

/**
 * A small "i" that explains a control on hover, so the UI stays uncluttered.
 * Uses the native tooltip (title) and is focusable for keyboard users.
 */
export const InfoTip = ({ text, className }: InfoTipProps) => (
    <span className={cx(s.infoTip, className)} title={text} aria-label={text} role="img" tabIndex={0}>
        <FaInfoCircle aria-hidden="true" />
    </span>
)

// ---------------------------------------------------------------- ProgressBar
export interface ProgressBarProps {
    /** Completed amount, clamped to 0–max. */
    value: number
    max?: number
    label?: ReactNode
    showPercentage?: boolean
    tone?: 'accent' | 'good' | 'bad' | 'warn'
    thin?: boolean
    className?: string
}

export const ProgressBar = ({ value, max = 100, label, showPercentage = true, tone = 'accent', thin = false, className }: ProgressBarProps) => {
    const safeMax = max > 0 ? max : 100
    const pct = Math.round((Math.min(Math.max(value, 0), safeMax) / safeMax) * 100)
    return (
        <div className={cx(s.progress, className)}>
            {(label || showPercentage) && (
                <div className={s.progressMeta}>
                    {label && <span className={s.progressLabel}>{label}</span>}
                    {showPercentage && <span className={s.progressPct}>{pct}%</span>}
                </div>
            )}
            <div className={cx(s.track, thin && s.thin)} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={typeof label === 'string' ? label : 'Progress'}>
                <div className={cx(s.fill, tone !== 'accent' && s[tone])} style={{ width: `${pct}%` }} />
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------- Steps
export interface StepItem {
    label: ReactNode
    /** Optional detail under the label (date). */
    meta?: ReactNode
}

export interface StepsProps {
    steps: StepItem[]
    /** Index of the current step; everything before is done. */
    current: number
    className?: string
}

/** Horizontal milestone tracker. */
export const Steps = ({ steps, current, className }: StepsProps) => (
    <ol className={cx(s.steps, className)}>
        {steps.map((step, i) => {
            const state = i < current ? 'done' : i === current ? 'current' : 'todo'
            return (
                <li key={i} className={cx(s.step, state === 'done' && s.done, state === 'current' && s.current)} aria-current={state === 'current' ? 'step' : undefined}>
                    <span className={s.stepDot}>{state === 'done' && <FaCheck aria-hidden="true" />}</span>
                    <span className={s.stepLabel}>
                        {step.label}
                        {step.meta && (
                            <>
                                <br />
                                <small>{step.meta}</small>
                            </>
                        )}
                    </span>
                </li>
            )
        })}
    </ol>
)

// ------------------------------------------------------------------- FileTile
export interface FileTileProps {
    url: string
    name: string
    /** Extension or mime hint used to decide on an image preview. */
    type?: string
    className?: string
}

const IMAGE_TYPES = new Set(['jpeg', 'jpg', 'png', 'gif', 'webp', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'])

/** Document tile with preview and a View action. */
export const FileTile = ({ url, name, type = '', className }: FileTileProps) => {
    const isImage = IMAGE_TYPES.has(type.toLowerCase())
    const isPdf = type.toLowerCase().includes('pdf')
    return (
        <div className={cx(s.fileTile, className)}>
            <div className={s.filePreview}>{isImage ? <img src={url} alt={name} /> : isPdf ? <iframe title={name} src={`${url}#toolbar=0&view=FitH`} /> : <FaFileAlt aria-hidden="true" />}</div>
            <div className={s.fileMeta}>
                <div style={{ minWidth: 0 }}>
                    <div className={s.fileName} title={name}>
                        {name}
                    </div>
                    {type && <div className={s.fileType}>{type}</div>}
                </div>
                <Button as="a" href={url} target="_blank" rel="noreferrer" size="sm">
                    View
                </Button>
            </div>
        </div>
    )
}

// --------------------------------------------------------------------- Avatar
export const Avatar = ({ name, size = 'md', className }: { name: string; size?: 'md' | 'sm'; className?: string }) => (
    <span className={cx(s.avatar, size === 'sm' && s.sm, className)} aria-hidden="true">
        {name.trim().charAt(0) || '?'}
    </span>
)

// ----------------------------------------------------------------------- Menu
export interface MenuProps extends React.HTMLAttributes<HTMLDivElement> {
    children: ReactNode
}

/** Popover menu surface. Position it yourself (absolute within a relative parent). */
export const Menu = ({ className, children, ...rest }: MenuProps) => (
    <div className={cx(s.menu, className)} role="menu" {...rest}>
        {children}
    </div>
)

export interface MenuItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    icon?: ReactNode
    active?: boolean
    danger?: boolean
    /** Right-aligned extra (badge, shortcut). */
    trailing?: ReactNode
    href?: string
    children: ReactNode
}

export const MenuItem = ({ icon, active, danger, trailing, href, className, children, ...rest }: MenuItemProps) => {
    const cls = cx(s.menuItem, active && s.active, danger && s.danger, className)
    const content = (
        <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {icon}
                {children}
            </span>
            {trailing}
        </>
    )
    if (href) {
        return (
            <a href={href} className={cls} role="menuitem" onClick={rest.onClick as any}>
                {content}
            </a>
        )
    }
    return (
        <button type="button" className={cls} role="menuitem" {...rest}>
            {content}
        </button>
    )
}

export const MenuLabel = ({ children }: { children: ReactNode }) => <div className={s.menuLabel}>{children}</div>

// ---------------------------------------------------------------------- Toast
export type ToastTone = 'neutral' | 'good' | 'bad' | 'warn'

interface ToastRecord {
    id: string
    title: ReactNode
    description?: ReactNode
    tone?: ToastTone
    action?: { label: string; onClick: () => void }
    duration?: number
}

interface ToastApi {
    toast: (t: Omit<ToastRecord, 'id'>) => void
}

const ToastContext = createContext<ToastApi | undefined>(undefined)

const TOAST_ICON: Record<ToastTone, ReactNode> = {
    neutral: <FaInfoCircle />,
    good: <FaCheckCircle />,
    bad: <FaTimesCircle />,
    warn: <FaExclamationTriangle />,
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<ToastRecord[]>([])

    const remove = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), [])

    const toast = useCallback(
        (t: Omit<ToastRecord, 'id'>) => {
            const id = Math.random().toString(36).slice(2, 9)
            const duration = t.duration ?? 5000
            setToasts((prev) => [...prev, { ...t, id, duration }])
            if (duration > 0) setTimeout(() => remove(id), duration)
        },
        [remove]
    )

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            <div className={s.toastHost} aria-live="polite">
                {toasts.map((t) => {
                    const tone = t.tone ?? 'neutral'
                    return (
                        <div key={t.id} className={cx(s.toast, tone !== 'neutral' && s[tone])} role="status">
                            {TOAST_ICON[tone]}
                            <div className={s.toastBody}>
                                <span className={s.toastTitle}>{t.title}</span>
                                {t.description && <span className={s.toastDesc}>{t.description}</span>}
                            </div>
                            {t.action && (
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        t.action?.onClick()
                                        remove(t.id)
                                    }}
                                >
                                    {t.action.label}
                                </Button>
                            )}
                            <button type="button" className={s.toastClose} onClick={() => remove(t.id)} aria-label="Dismiss">
                                ×
                            </button>
                        </div>
                    )
                })}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = (): ToastApi => {
    const ctx = useContext(ToastContext)
    if (!ctx) throw new Error('useToast must be used within a ToastProvider')
    return ctx
}
